import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { SlidePanel } from '@/components/ui/slide-panel';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { aiChatService, ChatMessage as DBChatMessage } from '@/services/aiChatService';
import { DynamicWidget } from '@/components/dashboard/widgets/DynamicWidget';
import type { DynamicWidgetConfig } from '@/types/dashboard';
import { DashboardService } from '@/services/dashboardService';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import clsx from 'clsx';

// Compact markdown styling for the narrow chat widget (tables scroll horizontally).
type MDProps = { children?: React.ReactNode };
const chatMarkdownComponents = {
  p: ({ children }: MDProps) => <p className="text-sm leading-relaxed mb-2 last:mb-0">{children}</p>,
  ul: ({ children }: MDProps) => <ul className="text-sm list-disc pl-4 mb-2 space-y-1">{children}</ul>,
  ol: ({ children }: MDProps) => <ol className="text-sm list-decimal pl-4 mb-2 space-y-1">{children}</ol>,
  li: ({ children }: MDProps) => <li className="leading-snug">{children}</li>,
  strong: ({ children }: MDProps) => <strong className="font-semibold text-foreground">{children}</strong>,
  a: ({ children, href }: MDProps & { href?: string }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline">{children}</a>
  ),
  code: ({ children }: MDProps) => (
    <code className="px-1 py-0.5 rounded bg-foreground/10 text-xs font-mono">{children}</code>
  ),
  table: ({ children }: MDProps) => (
    <div className="overflow-x-auto my-2">
      <table className="text-xs border-collapse w-full">{children}</table>
    </div>
  ),
  th: ({ children }: MDProps) => (
    <th className="border border-border px-2 py-1 text-left font-semibold bg-muted/60">{children}</th>
  ),
  td: ({ children }: MDProps) => <td className="border border-border px-2 py-1 align-top">{children}</td>,
};

// Icons
const MessageCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/>
  </svg>
);

const SendIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"/>
    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
);

interface DisplayMessage {
  id: string;
  text: string;
  sender: 'user' | 'ai' | 'system';
  timestamp: Date;
  isError?: boolean;
  widget?: DynamicWidgetConfig;   // AIC-4.1: a generated report to render inline
  widgetSaved?: boolean;
}

export const AiChatWidgetV2: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [messageOffset, setMessageOffset] = useState(0);
  const [totalMessageCount, setTotalMessageCount] = useState(0);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const { currentTenant } = useTenant();
  const navigate = useNavigate();

  // Initialize chat session when opened
  useEffect(() => {
    if (isOpen && user?.id && !sessionId && !isInitializing) {
      initializeChat();
    }
  }, [isOpen, user, sessionId]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (isOpen && messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      
      const scrollViewport = document.querySelector('.chat-scroll-viewport-v2');
      if (scrollViewport) {
        setTimeout(() => {
          scrollViewport.scrollTop = scrollViewport.scrollHeight;
        }, 100);
      }
    }
  }, [messages, isOpen]);

  const initializeChat = async () => {
    if (!user?.id || isInitializing) return;
    
    setIsInitializing(true);
    try {
      // Get or create session
      const session = await aiChatService.getOrCreateSession(user.id);
      if (!session) {
        console.error('Failed to create chat session');
        setMessages([{
          id: 'error-init',
          text: 'Failed to initialize chat. Please refresh and try again.',
          sender: 'system',
          timestamp: new Date(),
          isError: true
        }]);
        return;
      }
      
      setSessionId(session.id);
      
      // Load only the last 10 messages initially
      const { messages: history, total, hasMore } = await aiChatService.getChatHistoryPaginated(session.id, 10, 0);
      setTotalMessageCount(total);
      setHasMoreMessages(hasMore);
      setMessageOffset(0);
      
      if (history.length > 0) {
        // Convert database messages to display format
        const displayMessages: DisplayMessage[] = [];
        history.forEach((msg: DBChatMessage) => {
          // Add user message
          displayMessages.push({
            id: `${msg.id}-user`,
            text: msg.message,
            sender: 'user',
            timestamp: new Date(msg.created_at)
          });
          
          // Add AI response if exists
          if (msg.response) {
            displayMessages.push({
              id: `${msg.id}-ai`,
              text: msg.response,
              sender: msg.message_type === 'error' ? 'system' : 'ai',
              timestamp: new Date(msg.created_at),
              isError: msg.message_type === 'error'
            });
          }
        });
        setMessages(displayMessages);
        
        // If there are more messages, update the offset for next load
        if (hasMore) {
          setMessageOffset(history.length);
        }
      } else {
        // Show welcome message for new session
        const hour = new Date().getHours();
        const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
        const firstName = user?.user_metadata?.first_name || user?.email?.split('@')[0] || 'there';
        
        setMessages([{
          id: 'welcome',
          text: `${greeting}, ${firstName}! I'm your personal assistant. How can I help you today?`,
          sender: 'ai',
          timestamp: new Date()
        }]);
      }
    } catch (error) {
      console.error('Error initializing chat:', error);
      setMessages([{
        id: 'error-init',
        text: 'Failed to initialize chat. Please refresh and try again.',
        sender: 'system',
        timestamp: new Date(),
        isError: true
      }]);
    } finally {
      setIsInitializing(false);
    }
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  // Load more messages when scrolling to top
  const loadMoreMessages = async () => {
    if (!sessionId || isLoadingMore || !hasMoreMessages) return;
    
    setIsLoadingMore(true);
    try {
      // Calculate the next offset based on total messages and current messages
      const currentMessageCount = Math.floor(messages.length / 2); // Divide by 2 because each message has user + AI parts
      const nextOffset = totalMessageCount - currentMessageCount - 10; // Load 10 more from earlier
      const actualOffset = Math.max(0, nextOffset);
      
      const { messages: olderMessages, hasMore } = await aiChatService.getChatHistoryPaginated(
        sessionId,
        10,
        actualOffset
      );
      
      if (olderMessages.length > 0) {
        // Convert to display format
        const displayMessages: DisplayMessage[] = [];
        olderMessages.forEach((msg: DBChatMessage) => {
          displayMessages.push({
            id: `${msg.id}-user`,
            text: msg.message,
            sender: 'user',
            timestamp: new Date(msg.created_at)
          });
          
          if (msg.response) {
            displayMessages.push({
              id: `${msg.id}-ai`,
              text: msg.response,
              sender: msg.message_type === 'error' ? 'system' : 'ai',
              timestamp: new Date(msg.created_at),
              isError: msg.message_type === 'error'
            });
          }
        });
        
        // Save scroll height before adding messages
        const scrollViewport = document.querySelector('.chat-scroll-viewport-v2 > div[data-radix-scroll-area-viewport]') as HTMLDivElement;
        const previousScrollHeight = scrollViewport?.scrollHeight || 0;
        
        // Prepend older messages
        setMessages(prev => [...displayMessages, ...prev]);
        
        // Restore scroll position after messages are added
        setTimeout(() => {
          if (scrollViewport) {
            const newScrollHeight = scrollViewport.scrollHeight;
            const scrollDiff = newScrollHeight - previousScrollHeight;
            scrollViewport.scrollTop += scrollDiff;
          }
        }, 10);
        
        setHasMoreMessages(hasMore);
      }
    } catch (error) {
      console.error('Error loading more messages:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Handle scroll event with debouncing
  useEffect(() => {
    if (!isOpen) return;
    
    let scrollTimeout: NodeJS.Timeout;
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLDivElement;
      
      // Clear previous timeout
      clearTimeout(scrollTimeout);
      
      // Debounce scroll events
      scrollTimeout = setTimeout(() => {
        // Check if scrolled near top (within 100px)
        if (target.scrollTop < 100 && hasMoreMessages && !isLoadingMore) {
          loadMoreMessages();
        }
      }, 300);
    };
    
    // Get the actual scroll viewport from ScrollArea
    const scrollViewport = document.querySelector('.chat-scroll-viewport-v2 > div[data-radix-scroll-area-viewport]') as HTMLDivElement;
    if (scrollViewport) {
      scrollViewport.addEventListener('scroll', handleScroll);
      
      return () => {
        clearTimeout(scrollTimeout);
        scrollViewport.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isOpen, hasMoreMessages, isLoadingMore, sessionId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
  };

  const handleSendMessage = async () => {
    if (inputValue.trim() === '' || isLoading || !sessionId || !user?.id) return;

    const userMessage: DisplayMessage = {
      id: `user-${Date.now()}`,
      sender: 'user',
      text: inputValue.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    // Placeholder AI message we stream tokens into (progressive render).
    const aiId = `ai-${Date.now()}`;
    setMessages(prev => [...prev, {
      id: aiId,
      sender: 'ai',
      text: '',
      timestamp: new Date()
    }]);

    // Smooth typewriter reveal: network chunks arrive a few words at a time,
    // so we buffer them and reveal characters at a steady cadence (decoupled
    // from chunk arrival) for a "being written" feel. Hoisted above try so the
    // catch can stop the loop on error.
    const target = { text: '' };   // full text received so far
    let shown = 0;                 // chars currently displayed
    let streamDone = false;

    try {
      const reveal = new Promise<void>((resolve) => {
        const tick = () => {
          if (shown < target.text.length) {
            // Ease-out: reveal faster when far behind, slower near the tail.
            const remaining = target.text.length - shown;
            shown = Math.min(target.text.length, shown + Math.max(1, Math.ceil(remaining / 10)));
            const slice = target.text.slice(0, shown);
            setMessages(prev => prev.map(m => (m.id === aiId ? { ...m, text: slice } : m)));
          }
          if (streamDone && shown >= target.text.length) {
            resolve();
            return;
          }
          window.setTimeout(tick, 15);
        };
        tick();
      });

      // Stream the reply; deltas feed the buffer (not the bubble directly).
      const response = await aiChatService.sendMessage(
        userMessage.text,
        sessionId,
        user.id,
        (delta) => { target.text += delta; },
        (config) => {
          // AIC-4.1: a generated report widget — attach it to the AI message.
          setMessages(prev => prev.map(m =>
            m.id === aiId ? { ...m, widget: config as DynamicWidgetConfig } : m
          ));
        }
      );

      // Let the reveal drain to the final text, then settle the message.
      target.text = response;
      streamDone = true;
      await reveal;
      setMessages(prev => prev.map(m =>
        m.id === aiId
          ? { ...m, text: response, isError: response.includes('error') || response.includes('Error') }
          : m
      ));
    } catch (error) {
      console.error('AI chat error:', error);
      streamDone = true; // stop the reveal loop

      const errorMessage: DisplayMessage = {
        id: `error-${Date.now()}`,
        sender: 'system',
        text: 'Sorry, I couldn\'t process your message. Please try again.',
        timestamp: new Date(),
        isError: true
      };

      // Drop the empty streaming placeholder, then show the error.
      setMessages(prev => [...prev.filter(m => m.id !== aiId), errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // AIC-4.1: save a generated report widget to the user's default dashboard.
  const handleSaveWidget = async (messageId: string, config: DynamicWidgetConfig) => {
    try {
      const dash =
        (await DashboardService.getDefaultDashboard()) || (await DashboardService.getDashboards())[0];
      if (!dash) {
        setMessages(prev => [...prev, {
          id: `sys-${Date.now()}`, sender: 'system',
          text: 'No dashboard found to save to — create a dashboard first.',
          timestamp: new Date(), isError: true,
        }]);
        return;
      }
      await DashboardService.addWidget(dash.id, {
        widget_type_id: null,
        position: { x: 0, y: 0, w: 6, h: 4 },
        config,
        is_dynamic: true,
      });
      setMessages(prev => prev.map(m => (m.id === messageId ? { ...m, widgetSaved: true } : m)));
      setMessages(prev => [...prev, {
        id: `sys-${Date.now()}`, sender: 'system',
        text: `Saved "${config.display?.title || 'report'}" to ${dash.name}.`,
        timestamp: new Date(),
      }]);
    } catch (e) {
      console.error('Save widget failed:', e);
      setMessages(prev => [...prev, {
        id: `sys-${Date.now()}`, sender: 'system',
        text: 'Could not save the report to a dashboard.', timestamp: new Date(), isError: true,
      }]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends; Shift+Enter inserts a newline.
    if (e.key === 'Enter' && !e.shiftKey && !isLoading) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Custom header content for SlidePanel
  const headerContent = (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-3">
        <div>
          <h2 className="text-lg font-semibold">{currentTenant?.company_name?.trim() || 'Joules'} AI Assistant</h2>
          <p className="text-xs text-muted-foreground">Support Chat</p>
        </div>
      </div>
      <Button 
        variant="outline" 
        size="sm" 
        className="text-xs font-medium mr-2"
        onClick={() => navigate('/app/support-tickets')}
      >
        Tickets
      </Button>
    </div>
  );

  // Footer content with input and send button
  const footerContent = (
    <div className="flex w-full space-x-2">
      <Textarea
        placeholder={sessionId ? "Type your message..." : "Initializing..."}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        disabled={isLoading || !sessionId || isInitializing}
        rows={1}
        className="flex-grow min-h-[40px] max-h-[120px] resize-none px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
      />
      <Button 
        onClick={handleSendMessage} 
        disabled={isLoading || inputValue.trim() === '' || !sessionId || isInitializing} 
        className="bg-primary hover:bg-primary/90 text-primary-foreground transition-colors"
        aria-label="Send message"
      >
        <SendIcon />
      </Button>
    </div>
  );

  // Visibility. ⚠️ Deliberately AFTER every hook: this used to sit above three
  // useEffect calls, so a render where `user` was still null ran fewer hooks than
  // the next one — the "Rendered more hooks than during the previous render" crash,
  // avoided only because MainLayout does not mount this until permissions resolve.
  //
  // The real gate is MainLayout.tsx: it renders this widget only when the caller
  // holds `system.override`. A five-address allowlist used to sit here too and was
  // DEAD: MainLayout already refused non-superadmins, and superadmins skipped it.
  // Removed in CLN-6.2. Widening access is MainLayout's decision, not this file's.
  if (!user) {
    return null;
  }

  return (
    <>
      {/* SlidePanel for chat */}
      <SlidePanel
        isOpen={isOpen}
        onClose={toggleChat}
        headerContent={headerContent}
        footer={footerContent}
        width="md"
        position="right"
        topOffset="3.5rem"
        showBackdrop={true}
        backdropClassName="bg-transparent backdrop-blur-none"
        closeOnBackdropClick={true}
        showCloseButton={true}
        panelClassName="bg-background border-t border-l-2 border-border shadow-2xl shadow-[-8px_0_20px_-5px_rgba(0,0,0,0.15)]"
        headerClassName="py-3 px-6"
        contentClassName="p-0"
        footerClassName="bg-background border-t"
      >
        <div className="relative h-full">
          <ScrollArea className="h-full chat-scroll-viewport-v2" ref={scrollAreaRef}>
            <div ref={scrollContainerRef} className="flex flex-col px-4 py-3 gap-3">
              {isInitializing && (
              <div className="flex justify-center items-center py-4">
                <div className="text-sm text-primary">Initializing chat...</div>
              </div>
            )}
            
            {/* Loading indicator for older messages */}
            {isLoadingMore && (
              <div className="flex justify-center items-center py-2">
                <div className="text-xs text-muted-foreground flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full border-2 border-primary border-t-transparent animate-spin"></div>
                  Loading older messages...
                </div>
              </div>
            )}
            
            {/* Message count indicator */}
            {!isInitializing && totalMessageCount > 0 && hasMoreMessages && (
              <div className="flex justify-center items-center py-1">
                <div className="text-xs text-muted-foreground">
                  Showing recent {Math.min(Math.floor(messages.length / 2), totalMessageCount)} of {totalMessageCount} messages
                </div>
              </div>
            )}
            
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={clsx(
                  'flex items-start gap-3',
                  msg.sender === 'user' ? 'justify-end' : 'justify-start'
                )}
              >
                {msg.sender !== 'user' && (
                  <Avatar className="w-8 h-8 flex-shrink-0">
                    <AvatarImage 
                      src={msg.sender === 'system' ? '/system-icon.png' : '/ava.png'} 
                      alt="AI Assistant" 
                    />
                    <AvatarFallback className={msg.sender === 'system' ? 'bg-destructive' : 'bg-primary text-primary-foreground'}>
                      {msg.sender === 'system' ? '!' : 'AI'}
                    </AvatarFallback>
                  </Avatar>
                )}
                <div
                  className={clsx(
                    'max-w-[80%] px-4 py-3 rounded-lg shadow-sm',
                    msg.sender === 'user'
                      ? 'bg-primary/10 border border-primary/20 text-foreground rounded-br-none'
                      : msg.isError
                      ? 'bg-destructive/10 border border-destructive/20 text-destructive-foreground rounded-bl-none'
                      : 'bg-muted border border-border text-foreground rounded-bl-none'
                  )}
                >
                  {msg.sender === 'ai' && msg.text === '' ? (
                    // Streaming placeholder before the first token arrives.
                    <div className="flex space-x-1.5 py-1" aria-label="Assistant is typing">
                      <span className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  ) : msg.sender === 'ai' ? (
                    <div className="text-sm leading-relaxed break-words">
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={chatMarkdownComponents}>
                        {msg.text}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.text}</p>
                  )}
                  {msg.widget && (
                    <div className="mt-2 bg-background border border-border rounded-md p-2">
                      <div className="h-64">
                        <DynamicWidget config={msg.widget} />
                      </div>
                      <div className="flex justify-end mt-2">
                        {msg.widgetSaved ? (
                          <span className="text-xs text-muted-foreground">Saved to dashboard ✓</span>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => handleSaveWidget(msg.id, msg.widget!)}>
                            Save to dashboard
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-1 text-right">
                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                {msg.sender === 'user' && (
                  <Avatar className="h-8 w-8 flex-shrink-0 border border-border">
                    {user?.user_metadata?.avatar_url ? (
                      <AvatarImage src={user.user_metadata.avatar_url} alt={user.email || 'User'} />
                    ) : (
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {user?.email?.[0]?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    )}
                  </Avatar>
                )}
              </div>
            ))}
            {/* Typing indicator now renders inside the streaming AI bubble
                (the empty-text placeholder), so no separate block is needed. */}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>
        </div>
      </SlidePanel>
      
      {/* Floating button to open chat */}
      <Button
        onClick={toggleChat}
        className="fixed bottom-4 right-4 md:bottom-6 md:right-6 lg:bottom-8 lg:right-8 z-40 rounded-full w-14 h-14 p-0 shadow-lg bg-primary hover:bg-primary/90 text-primary-foreground flex items-center justify-center transition-transform duration-300 ease-in-out hover:scale-105"
        aria-label="Open AI Chat V2"
        title="AI Support Chat V2 (Beta)"
      >
        <MessageCircleIcon />
      </Button>
    </>
  );
};